import React, { useState, useEffect } from 'react';
import api from '../../api/axiosConfig';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactQuill from 'react-quill-new'; // Using the fixed version
import 'react-quill-new/dist/quill.snow.css'; // Import styles
import { 
    PaperAirplaneIcon, 
    ServerIcon, 
    KeyIcon, 
    PencilSquareIcon,
    XCircleIcon,
    EnvelopeIcon
} from '@heroicons/react/24/outline';
import T from '../../components/common/T';

const SMTPSettings = () => {
    const [loading, setLoading] = useState(false);
    const [testing, setTesting] = useState(false);
    
    // SMTP State
    const [smtp, setSmtp] = useState({
        host: '',
        port: '',
        user: '',
        pass: '',
        senderName: 'CMS Admin',
        encryption: 'ssl'
    });

    // Templates State
    const [templates, setTemplates] = useState([]);
    const [editingTemplate, setEditingTemplate] = useState(null);

    // 🟢 NEW: DEFINE THE PROFESSIONAL TOOLBAR
    // This gives you the buttons for Color, Fonts, Alignment, etc.
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }], // Headers
            ['bold', 'italic', 'underline', 'strike'], // Basic Styling
            [{ 'color': [] }, { 'background': [] }], // 🟢 Color Picker & Highlight
            [{ 'list': 'ordered'}, { 'list': 'bullet' }], // Lists
            [{ 'align': [] }], // 🟢 Alignment (Left, Center, Right)
            ['link', 'clean'] // Links & Clear Formatting
        ],
    };

    // --- 1. LOAD DATA ON MOUNT ---
    useEffect(() => {
        fetchSettings();
        fetchTemplates();
    }, []);

    const fetchSettings = async () => {
        try {
            const res = await api.get('/settings/smtp');
            if (res.data?.host || res.data?.data?.host) {
                const data = res.data?.data || res.data;
                setSmtp({
                    ...data,
                    pass: '' // Keep password empty for security
                });
            }
        } catch (err) {
            console.error("Failed to load SMTP", err);
        }
    };

    const fetchTemplates = async () => {
        try {
            const res = await api.get('/settings/templates');
            setTemplates(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch (err) {
            console.error("Failed to load templates", err);
        }
    };

    // --- 2. HANDLERS ---
    const handleSmtpChange = (e) => {
        setSmtp({ ...smtp, [e.target.name]: e.target.value });
    };

    const handleSaveSmtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post('/settings/smtp', smtp);
            toast.success("SMTP Settings Saved Successfully!");
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to save settings");
        } finally {
            setLoading(false);
        }
    };

    const handleTestConnection = async () => {
        setTesting(true);
        try {
            await api.post('/settings/smtp/test', smtp);
            toast.success("Connection Successful! Test email sent.");
        } catch (err) {
            toast.error(err.response?.data?.error || "Connection Failed");
        } finally {
            setTesting(false);
        }
    };

    const handleUpdateTemplate = async (e) => {
        e.preventDefault();
        try {
            await api.put(`/settings/templates/${editingTemplate.id}`, editingTemplate);
            toast.success("Template Updated!");
            setEditingTemplate(null);
            fetchTemplates(); 
        } catch (err) {
            toast.error("Failed to update template");
        }
    };

    return (
        <div className="space-y-8">

            {/* CARD 1: SMTP CONFIGURATION */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center gap-3 mb-6 border-b pb-4">
                    <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                        <ServerIcon className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-gray-900"><T>SMTP Server Configuration</T></h3>
                        <p className="text-sm text-gray-500"><T>Configure your email provider (Gmail, Outlook, AWS SES)</T></p>
                    </div>
                </div>

                <form onSubmit={handleSaveSmtp} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2"><T>SMTP Host</T></label>
                        <input type="text" name="host" value={smtp.host} onChange={handleSmtpChange} placeholder="smtp.gmail.com" className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2"><T>Port</T></label>
                        <input type="number" name="port" value={smtp.port} onChange={handleSmtpChange} placeholder="465 (SSL)" className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2"><T>User Email</T></label>
                        <input type="email" name="user" value={smtp.user} onChange={handleSmtpChange} placeholder="admin@company.com" className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2"><T>App Password</T></label>
                        <div className="relative">
                            <input type="password" name="pass" value={smtp.pass} onChange={handleSmtpChange} placeholder="••••••••••••••••" className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 pr-10" />
                            <KeyIcon className="w-5 h-5 text-gray-400 absolute right-3 top-3.5" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2"><T>Sender Name</T></label>
                        <input type="text" name="senderName" value={smtp.senderName} onChange={handleSmtpChange} className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500" />
                    </div>
                    
                    <div className="md:col-span-2 flex justify-end gap-3 mt-4 pt-4 border-t">
                        <button type="button" onClick={handleTestConnection} disabled={testing} className="px-4 py-2 bg-gray-100 text-gray-700 font-bold rounded-lg hover:bg-gray-200 flex items-center gap-2">
                            {testing ? <span className="animate-spin">↻</span> : <PaperAirplaneIcon className="w-5 h-5" />}
                            Test Connection
                        </button>
                        <button type="submit" disabled={loading} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-md">
                            {loading ? 'Saving...' : 'Save Configuration'}
                        </button>
                    </div>
                </form>
            </div>

            {/* CARD 2: TEMPLATE EDITOR LIST */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center gap-3 mb-6 border-b pb-4">
                    <div className="p-2 bg-amber-100 text-amber-600 rounded-lg">
                        <EnvelopeIcon className="w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-gray-900"><T>Notification Templates</T></h3>
                        <p className="text-sm text-gray-500"><T>Customize the emails sent by the system.</T></p>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                    {templates.map(tpl => (
                        <div key={tpl.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h4 className="font-bold text-gray-800">{tpl.name}</h4>
                                    <p className="text-sm text-gray-500 mt-1">Subject: {tpl.subject}</p>
                                </div>
                                <button onClick={() => setEditingTemplate(tpl)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                                    <PencilSquareIcon className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* EDIT MODAL WITH QUILL */}
            {editingTemplate && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="bg-gray-100 p-4 border-b flex justify-between items-center flex-shrink-0">
                            <h3 className="font-bold text-base"><T>Edit Template: {editingTemplate.name}</T></h3>
                            <button onClick={() => setEditingTemplate(null)}><XCircleIcon className="w-6 h-6 text-gray-500"/></button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Subject Line</label>
                                    <input 
                                        className="w-full p-2 border rounded" 
                                        value={editingTemplate.subject} 
                                        onChange={e => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                                    />
                                </div>
                                
                                {/* 🟢 REPLACED TEXTAREA WITH REACT QUILL & ADDED MODULES */}
                                <div className="h-64 mb-12">
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Message Body</label>
                                    <ReactQuill 
                                        theme="snow"
                                        value={editingTemplate.body_content}
                                        onChange={(content) => setEditingTemplate({...editingTemplate, body_content: content})}
                                        modules={quillModules} // 🟢 THIS ENABLES THE COLOR PICKER & TOOLS
                                        style={{ height: '200px' }}
                                    />
                                </div>

                                <p className="text-xs text-gray-500 mt-2">
                                    Available Variables: <span className="bg-gray-200 px-1 rounded">{'{{name}}'}</span> <span className="bg-gray-200 px-1 rounded">{'{{email}}'}</span> <span className="bg-gray-200 px-1 rounded">{'{{password}}'}</span>
                                </p>
                            </div>
                        </div>

                        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2 flex-shrink-0">
                            <button onClick={() => setEditingTemplate(null)} className="px-4 py-2 border rounded text-gray-600">Cancel</button>
                            <button onClick={handleUpdateTemplate} className="px-6 py-2 bg-blue-600 text-white font-bold rounded hover:bg-blue-700">Save Changes</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SMTPSettings;